/**
 * DisputeAccountsPickerModal
 * ──────────────────────────────────────────────────────────────────────────
 * 2026-02-28 (user request) — Sits between "Create Dispute Letter" and the
 * full DisputeLetterModal.  Lists every parsed account (including the
 * leftovers that didn't fit onto the 5-section FTC report) with a checkbox
 * next to each one.  All boxes are checked by default; the admin unchecks
 * anything they don't want on the letter (e.g. accounts that were already
 * disputed in a previous round) and clicks Continue.  The confirmed subset
 * is then passed to the DisputeLetterModal as `defaultAccountsStructured`.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { X, FileText, Check, Square, CheckSquare } from 'lucide-react';

export default function DisputeAccountsPickerModal({
  open,
  onClose,
  accounts,          // Array<{account_name, category, date_opened, balance}>
  onConfirm,         // (subset) => void
}) {
  // Persist checked-state across re-renders while the modal is open.  Keyed
  // by index because two rows can share the same account_name (rare but
  // possible when the user parsed a screenshot twice).
  const [checkedIdx, setCheckedIdx] = useState(() => new Set());

  useEffect(() => {
    if (!open) return;
    // Default = every account checked.  Admin unchecks the ones to drop.
    setCheckedIdx(new Set(accounts.map((_, i) => i)));
  }, [open, accounts]);

  const toggle = (i) => {
    setCheckedIdx((prev) => {
      const next = new Set(prev);
      if (next.has(i)) next.delete(i);
      else next.add(i);
      return next;
    });
  };
  const selectAll = () => setCheckedIdx(new Set(accounts.map((_, i) => i)));
  const clearAll = () => setCheckedIdx(new Set());

  const selectedCount = checkedIdx.size;
  const totalCount = accounts.length;

  // Group by category so the picker mirrors how the letter will render.
  const grouped = useMemo(() => {
    const buckets = {};
    accounts.forEach((a, i) => {
      const cat = (a.category || 'OTHER').toUpperCase();
      (buckets[cat] = buckets[cat] || []).push({ ...a, __idx: i });
    });
    return Object.entries(buckets).map(([cat, items]) => ({ cat, items }));
  }, [accounts]);

  const confirm = () => {
    const subset = accounts.filter((_, i) => checkedIdx.has(i));
    onConfirm?.(subset);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[105] bg-black/80 flex items-start sm:items-center justify-center p-3 overflow-y-auto"
      data-testid="dispute-accounts-picker-overlay"
    >
      <div className="w-full max-w-2xl bg-slate-900 border border-white/10 rounded-2xl shadow-2xl my-6">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <div>
            <h2 className="text-lg font-bold text-white flex items-center gap-2">
              <FileText className="w-5 h-5 text-fuchsia-400" />
              Which accounts should go on the dispute letter?
            </h2>
            <p className="text-xs text-slate-400 mt-1">
              Uncheck anything you don&apos;t want listed.  By default every parsed
              account is included — even the ones that didn&apos;t fit on the FTC
              report&apos;s 5 sections.
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-white/5 text-slate-400"
            data-testid="dispute-picker-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Toolbar */}
        <div className="flex items-center justify-between px-5 py-2 border-b border-white/5 bg-slate-950/40">
          <div className="text-sm text-slate-300">
            <span className="font-semibold text-fuchsia-300">{selectedCount}</span>
            <span className="text-slate-500"> / {totalCount} selected</span>
          </div>
          <div className="flex gap-2 text-xs">
            <button
              type="button"
              onClick={selectAll}
              className="px-3 py-1 rounded-md bg-white/5 hover:bg-white/10 text-slate-200 transition"
              data-testid="dispute-picker-select-all"
            >
              Select all
            </button>
            <button
              type="button"
              onClick={clearAll}
              className="px-3 py-1 rounded-md bg-white/5 hover:bg-white/10 text-slate-200 transition"
              data-testid="dispute-picker-clear-all"
            >
              Uncheck all
            </button>
          </div>
        </div>

        {/* List */}
        <div className="max-h-[55vh] overflow-y-auto p-4 space-y-4">
          {totalCount === 0 && (
            <div className="text-center text-slate-500 py-8 text-sm">
              No parsed accounts found.  Parse accounts first, then come back.
            </div>
          )}
          {grouped.map((g) => (
            <div key={g.cat}>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1.5 px-1">
                {g.cat}
              </div>
              <div className="space-y-1">
                {g.items.map((a) => {
                  const isChecked = checkedIdx.has(a.__idx);
                  return (
                    <label
                      key={a.__idx}
                      className={`flex items-center gap-3 px-3 py-2 rounded-lg border cursor-pointer transition ${
                        isChecked
                          ? 'border-fuchsia-400/40 bg-fuchsia-500/5 text-slate-100'
                          : 'border-white/5 bg-slate-950/40 text-slate-400'
                      }`}
                      data-testid={`dispute-picker-row-${a.__idx}`}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => toggle(a.__idx)}
                        className="sr-only"
                      />
                      {isChecked ? (
                        <CheckSquare className="w-5 h-5 text-fuchsia-400 flex-shrink-0" />
                      ) : (
                        <Square className="w-5 h-5 text-slate-500 flex-shrink-0" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className={`text-sm font-medium truncate ${isChecked ? '' : 'line-through'}`}>
                          {a.account_name || '(unnamed)'}
                        </div>
                        <div className="text-[11px] text-slate-500 flex items-center gap-2 mt-0.5">
                          {a.date_opened && <span>Opened {a.date_opened}</span>}
                          {a.balance && (
                            <span className="text-emerald-400">
                              ${String(a.balance).replace(/^\$/, '')}
                            </span>
                          )}
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-white/10 bg-slate-900/50 rounded-b-2xl">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm text-slate-300 hover:bg-white/5"
            data-testid="dispute-picker-cancel"
          >
            Cancel
          </button>
          <button
            onClick={confirm}
            disabled={selectedCount === 0}
            className="px-4 py-2 rounded-lg text-sm font-semibold bg-gradient-to-r from-fuchsia-600 to-pink-600 hover:from-fuchsia-500 hover:to-pink-500 text-white shadow-lg flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="dispute-picker-continue"
          >
            <Check className="w-4 h-4" />
            Continue with {selectedCount} account{selectedCount === 1 ? '' : 's'}
          </button>
        </div>
      </div>
    </div>
  );
}
